import type { OpsFlow, OpsPipelineStageId } from '../types';

/**
 * Canonical, left-to-right ordering of pipeline stages for the Overview board.
 * `uncategorized` is intentionally first so missing/legacy data is visible and
 * easy to triage, but it is a migration fallback, not a normal operating stage.
 */
export const OPS_PIPELINE_STAGES: ReadonlyArray<{ id: OpsPipelineStageId; label: string; description: string }> = [
  { id: 'uncategorized', label: 'Uncategorized', description: 'Missing stage data. Move to a real stage.' },
  { id: 'lead', label: 'Lead', description: 'Prospect or in screening; not yet committed.' },
  { id: 'committed', label: 'Committed', description: 'Verbal/contract commit secured.' },
  { id: 'prep', label: 'Prep', description: 'Getting ready: registration, billing, marketing, teacher.' },
  { id: 'launch', label: 'Launch', description: 'Final week and first-day readiness.' },
  { id: 'running', label: 'Running', description: 'Active and stabilized.' },
  { id: 'renewal', label: 'Renewal', description: 'Wrapping up; pitch next season.' },
];

export const OPS_PIPELINE_STAGE_IDS: ReadonlyArray<OpsPipelineStageId> = OPS_PIPELINE_STAGES.map((s) => s.id);

const STAGE_LABEL_BY_ID: Record<OpsPipelineStageId, string> = OPS_PIPELINE_STAGES.reduce((acc, s) => {
  acc[s.id] = s.label;
  return acc;
}, {} as Record<OpsPipelineStageId, string>);

export function pipelineStageLabel(id: OpsPipelineStageId | null | undefined): string {
  if (!id) return 'Uncategorized';
  return STAGE_LABEL_BY_ID[id] || 'Uncategorized';
}

function normalizeCheckpointTitle(title: string): string {
  return String(title || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

/**
 * Map a real checkpoint title to one of the 6 canonical stages.
 *
 * NOTE: This maps from the actual workflow semantics of the checkpoint titles,
 * NOT from the historically mislabeled `OVERVIEW_CATEGORIES` display strings
 * (where, e.g., the id `teacher_assign_ready` was shown as "Billing Information
 * Completed"). Real checkpoint order is roughly:
 *   Screening -> Commitment Ready -> Marketing -> Contract Safe ->
 *   Teacher Assign Ready -> 1 Week Ready -> First Day Ready -> Stabilized.
 */
export function mapCheckpointTitleToStage(title: string): OpsPipelineStageId | null {
  const t = normalizeCheckpointTitle(title);
  if (!t) return null;

  if (t.includes('stabilized')) return 'running';
  if (t.includes('first day') || (t.includes('first') && t.includes('day'))) return 'launch';
  if (t.includes('1 week') || t.includes('one week') || t.includes('week before')) return 'launch';
  if (t.includes('teacher') && t.includes('assign')) return 'prep';
  if (t.includes('contract safe') || (t.includes('registration') && t.includes('live'))) return 'prep';
  if (t.includes('billing')) return 'prep';
  if (t.includes('marketing')) return 'prep';
  if (t.includes('commitment') || t.includes('committment') || t.includes('committed')) return 'committed';
  if (t.includes('screening') || t.includes('screened') || t.includes('lead')) return 'lead';
  return null;
}

export type LegacyStageInput = {
  squadStatus?: string | null;
  hasCurrentCheckpoint: boolean;
  overallPercentComplete: number;
  allCheckpointsComplete?: boolean;
  currentCheckpointTitle?: string | null;
};

/**
 * Reproduce the legacy Overview bucketing into the new 6-stage model so flows
 * without an explicit `pipelineStage` still land somewhere sensible.
 */
export function inferLegacyStage(input: LegacyStageInput): OpsPipelineStageId {
  const status = String(input.squadStatus || '').trim().toLowerCase();
  if (status === 'lead') return 'lead';

  // Fully complete flow with no active checkpoint -> end of the pipeline.
  if (!input.hasCurrentCheckpoint && (input.overallPercentComplete >= 100 || Boolean(input.allCheckpointsComplete))) {
    return 'renewal';
  }

  // No template / no active checkpoint and not a lead: needs manual triage.
  if (!input.hasCurrentCheckpoint) return 'uncategorized';

  return mapCheckpointTitleToStage(input.currentCheckpointTitle || '') || 'uncategorized';
}

/**
 * Resolve the stage to display: the manual stage wins, otherwise infer from
 * legacy data. Keep this the single read path used by the board.
 */
export function resolvePipelineStage(
  flow: Pick<OpsFlow, 'pipelineStage'> | null | undefined,
  legacyInput: LegacyStageInput,
): OpsPipelineStageId {
  const manual = flow?.pipelineStage;
  if (manual && OPS_PIPELINE_STAGE_IDS.includes(manual)) return manual;
  return inferLegacyStage(legacyInput);
}
